import type { TenantSmtpConnectionFragment } from "~/gql/queries/tenantSmtpConnection.generated";
import type { SmtpAddressInput } from "~admin/email/helpers/smtpAddressListEditor";

import { SmtpFromMode, type SmtpSenderAddressInput } from "~/gql/graphqlBase.generated";
import { MAX_SMTP_ADDRESSES, normalizeSmtpAddressEmail } from "~admin/email/helpers/smtpAddressListEditor";

export interface SmtpConnectionFormInput {
	addressCount: number;
	fromMode: SmtpFromMode;
	host: string;
	password: string;
	port: number;
	singleAddressValid: boolean;
	username: string;
}

export interface SmtpConnectionDraft {
	addresses: SmtpAddressInput[];
	fromMode: SmtpFromMode;
	host: string;
	port: number;
	username: string;
}

const DEFAULT_SMTP_PORT = 587;

export const smtpConnectionDraft = (connection?: TenantSmtpConnectionFragment | null): SmtpConnectionDraft => ({
	addresses:
		connection?.addresses.map((address) => ({
			adminOnly: address.adminOnly,
			displayName: address.displayName,
			email: address.email
		})) ?? [],
	fromMode: connection?.fromMode ?? SmtpFromMode.SingleAccount,
	host: connection?.host ?? "",
	port: connection?.port ?? DEFAULT_SMTP_PORT,
	username: connection?.username ?? ""
});

const sameAddresses = (left: readonly SmtpAddressInput[], right: readonly SmtpAddressInput[]): boolean =>
	left.length === right.length &&
	left.every(
		(address, index) =>
			address.email === right[index].email &&
			address.displayName === right[index].displayName &&
			address.adminOnly === right[index].adminOnly
	);

export const hasSmtpConnectionEdits = (
	baseline: SmtpConnectionDraft,
	draft: SmtpConnectionDraft,
	passwordTyped: boolean
): boolean =>
	passwordTyped ||
	draft.host !== baseline.host ||
	draft.port !== baseline.port ||
	draft.username !== baseline.username ||
	draft.fromMode !== baseline.fromMode ||
	!sameAddresses(draft.addresses, baseline.addresses);

const isValidSmtpPort = (port: number): boolean => Number.isInteger(port) && port > 0 && port <= 65535;

export const canSubmitSmtpConnectionForm = (input: SmtpConnectionFormInput): boolean => {
	if (!input.host.trim() || !input.username.trim() || !input.password.trim()) return false;
	if (!isValidSmtpPort(input.port)) return false;
	if (input.fromMode !== SmtpFromMode.SingleAccount && input.addressCount > MAX_SMTP_ADDRESSES) return false;
	if (input.fromMode === SmtpFromMode.SingleAccount && !input.singleAddressValid) return false;
	if (input.fromMode === SmtpFromMode.DefinedList && input.addressCount === 0) return false;
	if (input.fromMode === SmtpFromMode.AnyWithList && input.addressCount === 0) return false;
	return true;
};

export const smtpSingleAddress = (email: string, baselineAddresses: readonly SmtpAddressInput[]): SmtpAddressInput => ({
	adminOnly: baselineAddresses.find(
		(address) => normalizeSmtpAddressEmail(address.email) === normalizeSmtpAddressEmail(email)
	)?.adminOnly,
	displayName: "",
	email
});

export const buildSmtpAddressesPayload = (
	fromMode: SmtpFromMode,
	addresses: readonly SmtpAddressInput[]
): SmtpSenderAddressInput[] => {
	const normalized = addresses.map((address) => ({
		adminOnly: address.adminOnly,
		displayName: address.displayName.trim() || undefined,
		email: normalizeSmtpAddressEmail(address.email)
	}));
	return fromMode === SmtpFromMode.SingleAccount ? normalized.slice(0, 1) : normalized;
};
